import { useState, useEffect } from 'react';
import { Container, Card, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getMyWallets, createWithdrawal, getMyWithdrawals, cancelWithdrawal, getWithdrawalLimit } from '../../api/walletApi';
import { getMe } from '../../api/authApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const CHARGE_RATE = 0.10;

const cardDark = {
  background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.06) 0%, #0d0d0d 65%)',
  border: '1px solid rgba(0,229,255,0.15)',
  color: '#fff',
};

const inputStyle = {
  width: '100%',
  background: 'rgba(0,229,255,0.04)',
  border: '1px solid rgba(0,229,255,0.2)',
  borderRadius: 8,
  padding: '10px 14px',
  color: '#fff',
  fontSize: 14,
  outline: 'none',
};

const labelStyle = {
  color: '#888',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  display: 'block',
  marginBottom: 6,
};

const statusColors = {
  pending:    { color: '#ffaa44', bg: 'rgba(255,170,68,0.12)', border: 'rgba(255,170,68,0.3)' },
  cancelled:  { color: '#aaa',    bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)' },
  rejected:   { color: '#ff6666', bg: 'rgba(200,50,50,0.12)', border: 'rgba(200,50,50,0.3)' },
  approved:   { color: '#66aaff', bg: 'rgba(102,170,255,0.12)', border: 'rgba(102,170,255,0.3)' },
  successful: { color: '#66ff99', bg: 'rgba(102,255,153,0.12)', border: 'rgba(102,255,153,0.3)' },
};

const StatusBadge = ({ status }) => {
  const s = statusColors[status] || statusColors.pending;
  return (
    <span style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
      {status}
    </span>
  );
};

const Withdraw = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [limitData, setLimitData] = useState(null);
  const [profileUsdtAddress, setProfileUsdtAddress] = useState(null);
  const [amount, setAmount] = useState('');
  const [destination, setDestination] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);

  const numAmount = parseFloat(amount) || 0;
  const charges = parseFloat((numAmount * CHARGE_RATE).toFixed(2));
  const receivable = parseFloat((numAmount - charges).toFixed(2));
  const isInsufficient = numAmount > 0 && balance !== null && numAmount > balance;
  const isOverLimit = numAmount > 0 && limitData !== null && numAmount > limitData.availableLimit;

  const fetchHistory = () =>
    getMyWithdrawals()
      .then((res) => setHistory(res.data.withdrawals))
      .finally(() => setLoadingHistory(false));

  const fetchLimit = () =>
    getWithdrawalLimit().then((res) => setLimitData(res.data)).catch(() => {});

  useEffect(() => {
    getMyWallets().then((res) => setBalance(res.data.wallets.earning_wallet?.balance ?? 0));
    getMe().then((res) => setProfileUsdtAddress(res.data.user?.usdtAddress || ''));
    fetchHistory();
    fetchLimit();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!destination) return setError('Please select a withdrawal destination.');
    if (destination === 'external' && !profileUsdtAddress) return setError('No USDT wallet address found. Please update it in My Profile.');
    if (isInsufficient) return setError('Insufficient balance.');

    setSubmitting(true);
    try {
      await createWithdrawal({ amount: numAmount, destination, externalAddress: profileUsdtAddress, comment });
      setSuccess(true);
      setAmount(''); setDestination(''); setComment('');
      setBalance((prev) => parseFloat((prev - numAmount).toFixed(2)));
      fetchHistory();
      fetchLimit();
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setError(err.response?.data?.message || 'Withdrawal failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    setCancellingId(id);
    try {
      await cancelWithdrawal(id);
      await getMyWallets().then((res) => setBalance(res.data.wallets.earning_wallet?.balance ?? 0));
      fetchHistory();
      fetchLimit();
    } catch (err) {
      setError(err.response?.data?.message || 'Cancel failed.');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4" style={{ maxWidth: 560 }}>
        <button onClick={() => navigate('/wallet?type=earning')} style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}>
          ‹ Back to Earning Wallet
        </button>

        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 20 }}>Withdraw Funds</h5>

        {/* Balance */}
        <Card style={cardDark} className="p-3 mb-3">
          <div style={{ color: '#888', fontSize: 11, marginBottom: 4 }}>EARNING WALLET BALANCE</div>
          <div>
            <span style={{ fontSize: 28, fontWeight: 800, color: '#66ff99' }}>{balance ?? '—'}</span>
            <span style={{ color: '#aaa', fontSize: 13, marginLeft: 8 }}>USDT</span>
          </div>
        </Card>

        {/* Withdrawal Limit */}
        {limitData && (
          <Card style={{ ...cardDark, border: '1px solid rgba(255,170,68,0.2)', marginBottom: 16 }} className="p-3">
            <div style={{ color: '#ffaa44', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
              Withdrawal Limit
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {[
                { label: 'Total Limit', value: limitData.totalLimit, color: '#ffaa44' },
                { label: 'Used', value: limitData.usedLimit, color: '#ff6666' },
                { label: 'Available', value: limitData.availableLimit, color: '#66ff99' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ textAlign: 'center', background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '8px 6px' }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color }}>{value.toFixed(2)}</div>
                  <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
            {limitData.totalLimit === 0 && (
              <div style={{ color: '#888', fontSize: 11, marginTop: 10, textAlign: 'center' }}>
                Buy slots to unlock your withdrawal limit.
              </div>
            )}
          </Card>
        )}

        {/* Form */}
        <Card style={cardDark} className="p-4 mb-4">
          <form onSubmit={handleSubmit}>

            {/* Amount */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Withdrawal Amount (USDT)</label>
              <input
                type="number"
                min="1"
                step="any"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setError(''); }}
                style={{ ...inputStyle, borderColor: isInsufficient || isOverLimit ? 'rgba(200,50,50,0.5)' : 'rgba(0,229,255,0.2)' }}
                required
              />
              {isInsufficient && <div style={{ color: '#ff6666', fontSize: 11, marginTop: 4 }}>Exceeds available balance.</div>}
              {!isInsufficient && isOverLimit && (
                <div style={{ color: '#ff6666', fontSize: 11, marginTop: 4 }}>
                  Exceeds withdrawal limit. Available limit: {limitData?.availableLimit?.toFixed(2)} USDT.
                </div>
              )}
            </div>

            {/* Destination */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Withdraw To</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {[{ val: 'one_wallet', label: 'OneWallet' }, { val: 'external', label: 'External USDT Address' }].map(({ val, label }) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => { setDestination(val); setError(''); }}
                    style={{
                      flex: 1,
                      padding: '10px 8px',
                      borderRadius: 8,
                      border: destination === val ? `1px solid ${cyan}` : '1px solid rgba(255,255,255,0.1)',
                      background: destination === val ? 'rgba(0,229,255,0.1)' : 'transparent',
                      color: destination === val ? cyan : '#888',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* External address — pulled from profile */}
            {destination === 'external' && (
              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Blockchain Address (USDT TRC-20 / BEP-20)</label>
                {profileUsdtAddress ? (
                  <div style={{
                    background: 'rgba(102,255,153,0.05)',
                    border: '1px solid rgba(102,255,153,0.2)',
                    borderRadius: 8,
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}>
                    <span style={{ color: '#66ff99', fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {profileUsdtAddress}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate('/profile')}
                      style={{ background: 'none', border: 'none', color: cyan, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', padding: 0 }}
                    >
                      Edit ›
                    </button>
                  </div>
                ) : (
                  <div style={{
                    background: 'rgba(255,170,68,0.06)',
                    border: '1px solid rgba(255,170,68,0.25)',
                    borderRadius: 8,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}>
                    <span style={{ color: '#ffaa44', fontSize: 13 }}>
                      No USDT wallet address saved in your profile.
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate('/profile')}
                      style={{ background: cyan, border: 'none', color: '#000', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      Update Profile →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Charges summary */}
            {numAmount > 0 && destination && (
              <div style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.12)', borderRadius: 8, padding: '12px 16px', marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ color: '#888', fontSize: 12 }}>Requested Amount</span>
                  <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{numAmount} USDT</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ color: '#888', fontSize: 12 }}>Service Charge (10%)</span>
                  <span style={{ color: '#ff6666', fontSize: 12, fontWeight: 600 }}>− {charges} USDT</span>
                </div>
                <div style={{ borderTop: '1px solid rgba(0,229,255,0.1)', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>You Receive</span>
                  <span style={{ color: '#66ff99', fontSize: 15, fontWeight: 800 }}>{receivable} USDT</span>
                </div>
              </div>
            )}

            {/* Comment */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Comment (Optional)</label>
              <textarea
                rows={2}
                placeholder="Add a note..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                style={{ ...inputStyle, resize: 'none', fontFamily: 'inherit' }}
              />
            </div>

            {error && <Alert variant="danger" style={{ background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', fontSize: 13 }}>{error}</Alert>}
            {success && <Alert style={{ background: 'rgba(102,255,153,0.1)', border: '1px solid rgba(102,255,153,0.3)', color: '#66ff99', fontSize: 13 }}>Withdrawal request submitted successfully.</Alert>}

            <button
              type="submit"
              disabled={submitting || isInsufficient || isOverLimit || !amount || !destination || (destination === 'external' && !profileUsdtAddress)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: 'none',
                background: submitting || isInsufficient || isOverLimit || !amount || !destination || (destination === 'external' && !profileUsdtAddress) ? 'rgba(255,255,255,0.05)' : cyan,
                color: submitting || isInsufficient || isOverLimit || !amount || !destination || (destination === 'external' && !profileUsdtAddress) ? '#555' : '#000',
                fontWeight: 700,
                fontSize: 15,
                cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Withdrawal'}
            </button>
          </form>
        </Card>

        {/* Limit Update Log */}
        {limitData?.logs?.length > 0 && (
          <>
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12, marginTop: 28 }}>
              Withdrawal Limit Log
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
              {limitData.logs.map((log) => (
                <div
                  key={log._id}
                  style={{
                    background: 'rgba(255,170,68,0.05)',
                    border: '1px solid rgba(255,170,68,0.15)',
                    borderRadius: 10,
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <div>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{log.comments}</div>
                    <div style={{ color: '#555', fontSize: 11, marginTop: 3 }}>
                      {fmtIST(log.createdAt)}
                      {' · '}{log.triggerType === 'self_activation' ? 'Self Activation' : 'First Referral'}
                    </div>
                  </div>
                  <div style={{ color: '#ffaa44', fontWeight: 800, fontSize: 16, whiteSpace: 'nowrap' }}>
                    +{log.limitAdded.toFixed(2)} USDT
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* History */}
        <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Withdrawal History</div>
        {loadingHistory ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" style={{ color: cyan }} /></div>
        ) : history.length === 0 ? (
          <div style={{ color: '#555', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>No withdrawal requests yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {history.map((w) => (
              <Card key={w._id} style={{ ...cardDark, padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{w.amount} USDT</div>
                    <div style={{ color: '#66ff99', fontSize: 12, marginTop: 2 }}>Receivable: {w.receivableAmount} USDT</div>
                    <div style={{ color: '#888', fontSize: 11, marginTop: 4 }}>
                      {w.destination === 'one_wallet' ? 'OneWallet' : 'External'}{w.externalAddress ? ` · ${w.externalAddress.slice(0, 12)}...` : ''}
                    </div>
                    {w.comment && <div style={{ color: '#666', fontSize: 11, marginTop: 2, fontStyle: 'italic' }}>{w.comment}</div>}
                    <div style={{ color: '#555', fontSize: 11, marginTop: 4 }}>
                      {fmtIST(w.createdAt)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                    <StatusBadge status={w.status} />
                    {w.status === 'pending' && (
                      <button
                        onClick={() => handleCancel(w._id)}
                        disabled={cancellingId === w._id}
                        style={{ background: 'transparent', border: '1px solid rgba(200,50,50,0.4)', color: '#ff6666', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}
                      >
                        {cancellingId === w._id ? '...' : 'Cancel'}
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
};

export default Withdraw;
